import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, MapPin, User, Phone, BookOpen, Copy, Clock, GraduationCap, MessageSquare } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import type { ClassTrial } from '@/hooks/useClassTrials';
import { CLASS_TRIAL_STATUS_OPTIONS, getTrialStatusColumnColor } from './classTrialStatusColors';

interface ClassTrialKanbanProps {
  trials: ClassTrial[];
  isLoading: boolean;
}

function TrialCard({ trial }: { trial: ClassTrial }) {
  const navigate = useNavigate();

  const studentDisplay = [trial.student_firstname, trial.student_lastname].filter(Boolean).join(' ');
  const fullName = studentDisplay
    ? `${studentDisplay}${trial.student_nickname ? ` (${trial.student_nickname})` : ''}`
    : trial.student_nickname || trial.student_name || 'ไม่ระบุชื่อ';

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    const lines = [
      `ชื่อ: ${fullName}`,
      trial.parent_phone ? `เบอร์ผู้ปกครอง: ${trial.parent_phone}` : null,
      trial.student_phone ? `เบอร์นักเรียน: ${trial.student_phone}` : null,
      trial.location ? `สถานที่: ${trial.location}` : null,
      trial.grade ? `ระดับชั้น: ${trial.grade}` : null,
      trial.subject ? `วิชา: ${trial.subject}` : null,
      trial.class_date ? `วันทดลองเรียน: ${trial.class_date}${trial.trial_time ? ` ${trial.trial_time}` : ''}` : null,
      trial.actual_trial_day ? `วันทดลองจริง: ${trial.actual_trial_day}${trial.actual_trial_time ? ` ${trial.actual_trial_time}` : ''}` : null,
      trial.desired_course ? `คอร์สที่ต้องการ: ${trial.desired_course}` : null,
      trial.teacher_code ? `ครู: ${trial.teacher_code}` : null,
      trial.parent_expectations_trial ? `ความคาดหวัง: ${trial.parent_expectations_trial}` : null,
    ].filter(Boolean).join('\n');
    navigator.clipboard.writeText(lines);
    toast({ title: 'คัดลอกแล้ว' });
  };

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => navigate(`/admin/class-trials/${trial.id}/edit`)}
    >
      <CardContent className="p-3 space-y-1.5">
        {/* Header: name + copy */}
        <div className="flex items-start justify-between gap-1">
          <div className="font-medium text-sm leading-tight">{fullName}</div>
          <button
            onClick={handleCopy}
            className="shrink-0 p-1 rounded hover:bg-muted"
            title="คัดลอก"
          >
            <Copy className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </div>

        {/* Phone */}
        {trial.parent_phone && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Phone className="h-3 w-3 shrink-0" />
            <span>ผู้ปกครอง: {trial.parent_phone}</span>
          </div>
        )}
        {trial.student_phone && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Phone className="h-3 w-3 shrink-0" />
            <span>นักเรียน: {trial.student_phone}</span>
          </div>
        )}

        {/* Location */}
        {trial.location && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3 shrink-0" />
            <span>{trial.location}</span>
          </div>
        )}

        {/* Grade + Subject badges */}
        <div className="flex flex-wrap gap-1">
          {trial.grade && (
            <Badge variant="outline" className="text-xs px-1.5 py-0">
              <GraduationCap className="h-3 w-3 mr-0.5" />{trial.grade}
            </Badge>
          )}
          {trial.subject && (
            <Badge variant="outline" className="text-xs px-1.5 py-0">
              <BookOpen className="h-3 w-3 mr-0.5" />{trial.subject}
            </Badge>
          )}
        </div>

        {/* Requested trial date/time */}
        {trial.class_date && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3 shrink-0" />
            <span>นัด: {trial.class_date}{trial.trial_time ? ` ${trial.trial_time}` : ''}</span>
          </div>
        )}

        {/* Actual trial date/time */}
        {trial.actual_trial_day && (
          <div className="flex items-center gap-1.5 text-xs text-blue-600">
            <Clock className="h-3 w-3 shrink-0" />
            <span>จริง: {trial.actual_trial_day}{trial.actual_trial_time ? ` ${trial.actual_trial_time}` : ''}</span>
          </div>
        )}

        {/* Desired course - text display */}
        {trial.desired_course && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <BookOpen className="h-3 w-3 shrink-0" />
            <span>คอร์ส: {trial.desired_course}</span>
          </div>
        )}

        {/* Teacher */}
        {trial.teacher_code && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <User className="h-3 w-3 shrink-0" />
            <span>{trial.teacher_code}</span>
          </div>
        )}

        {/* Parent expectations */}
        {trial.parent_expectations_trial && (
          <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <MessageSquare className="h-3 w-3 shrink-0 mt-0.5" />
            <span className="line-clamp-2">{trial.parent_expectations_trial}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function ClassTrialKanban({ trials, isLoading }: ClassTrialKanbanProps) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const groupedTrials: Record<string, ClassTrial[]> = {};
  CLASS_TRIAL_STATUS_OPTIONS.forEach((status) => {
    groupedTrials[status] = [];
  });
  const otherTrials: ClassTrial[] = [];

  trials.forEach((trial) => {
    const status = trial.status || '';
    if (groupedTrials[status]) {
      groupedTrials[status].push(trial);
    } else {
      otherTrials.push(trial);
    }
  });

  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {CLASS_TRIAL_STATUS_OPTIONS.map((status) => {
        const statusTrials = groupedTrials[status];
        return (
          <div
            key={status}
            className={`min-w-[300px] max-w-[340px] flex-shrink-0 bg-muted/30 rounded-lg border border-t-4 ${getTrialStatusColumnColor(status)}`}
          >
            <div className="p-3 border-b">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm truncate">{status}</span>
                <Badge variant="secondary" className="ml-2 text-xs">
                  {statusTrials.length}
                </Badge>
              </div>
            </div>
            <div className="p-2 space-y-2 max-h-[calc(100vh-350px)] overflow-y-auto">
              {statusTrials.length === 0 && (
                <div className="text-center py-6 text-muted-foreground text-xs">
                  ไม่มีรายการ
                </div>
              )}
              {statusTrials.map((trial) => (
                <TrialCard key={trial.id} trial={trial} />
              ))}
            </div>
          </div>
        );
      })}

      {otherTrials.length > 0 && (
        <div className="min-w-[300px] max-w-[340px] flex-shrink-0 bg-muted/30 rounded-lg border border-t-4 border-t-muted">
          <div className="p-3 border-b">
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm">อื่นๆ</span>
              <Badge variant="secondary" className="ml-2 text-xs">{otherTrials.length}</Badge>
            </div>
          </div>
          <div className="p-2 space-y-2 max-h-[calc(100vh-350px)] overflow-y-auto">
            {otherTrials.map((trial) => (
              <TrialCard key={trial.id} trial={trial} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
